/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2012, 2019
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 * borrowed from cclcore.
 * Common bidi javascript library - BidiUtils.
 * Singleton containing useable bidi methods.
 */

/**
 * This module contains a subset of BidiUtils from cclcore with some
 * modifications to meet gemini's requirements for BiDi text. Elements passed
 * in to the methods of this module is expected to contain only a single text
 * node such as <div contenteditable> or <input> elements
 */
define([], function () {
	'use strict';

	var AUTO = 'auto',
	    arabicLocales = {
		'General Info': {
			'Generated from': 'CLDR Version: 30.0.3',
			'Generated by': 'BDL CLDR Reader Tool',
			'Date': '16-03-17 12:40:12'
		},
		'Arabic Default Numbering Systems': {
			'ar': 'arab',
			'ar_AE': 'arab',
			'ar_BH': 'arab',
			'ar_DJ': 'arab',
			'ar_DZ': 'latn',
			'ar_EG': 'arab',
			'ar_EH': 'latn',
			'ar_ER': 'arab',
			'ar_IL': 'arab',
			'ar_IQ': 'arab',
			'ar_JO': 'arab',
			'ar_KM': 'arab',
			'ar_KW': 'arab',
			'ar_LB': 'arab',
			'ar_LY': 'latn',
			'ar_MA': 'latn',
			'ar_MR': 'arab',
			'ar_OM': 'arab',
			'ar_PS': 'arab',
			'ar_QA': 'arab',
			'ar_SA': 'arab',
			'ar_SD': 'arab',
			'ar_SO': 'arab',
			'ar_SS': 'arab',
			'ar_SY': 'arab',
			'ar_TD': 'arab',
			'ar_TN': 'latn',
			'ar_YE': 'arab'
		}
	},
	    cldrData = arabicLocales['Arabic Default Numbering Systems'];

	var BidiUtil = function BidiUtil() {
		this._isIE = this._detectIE(navigator.userAgent);
		this.userPreferredTextDir = this._getUserPreferredTextDir() || AUTO;
		this.LRE = '\u202A';
		this.RLE = '\u202B';
		this.PDF = '\u202C';
		this.LRM = '\u200E';
	};

	BidiUtil.prototype._detectIE = function (useragent) {
		return (/\b(MSIE|Trident|Edge)\b/.test(useragent)
		);
	};

	// Private Methods
	// Should not need to call these methods outside this module. Although real
	// encapsulation is possible, defining as part of BidiUtil's prototype allows
	// unit testing and faster scope chain access from public methods

	/**
  * Gets the user's preferred text direction from <html> attribute passed in
  * from server-side and stores it as member of this singleton as userPreferredTextDir
  * @return {string} User's preferred text direction (i.e. ltr, rtl, auto)
  */
	BidiUtil.prototype._getUserPreferredTextDir = function () {
		if (!this.userPreferredTextDir) {
			this.userPreferredTextDir = document.documentElement.getAttribute('data-pref-text-dir');
		}
		return this.userPreferredTextDir;
	};

	/**
  * Normalizes the process of retrieving the value from <input> or <div contenteditable>
  * HTML elements
  * @param  {Element|HTMLInputElement} element - DOM element receiving text input
  * @return {string} String value of the DOM element
  */
	BidiUtil.prototype._getNodeValue = function (element) {
		return element.value || element.tagName === 'INPUT' ? element.value : element.textContent;
	};

	/**
  * Normalizes the process of setting the value to <input> or <div contenteditable>
  * HTML elements
  * @param {Element|HTMLInputElement} element - DOM element receiving text input
  * @param {string} value - String value to be set to element
  */
	BidiUtil.prototype._setNodeValue = function (element, value) {
		if (element.value || element.tagName === 'INPUT') {
			element.value = value;
		} else {
			// setting the textContent removes any text of its child nodes
			element.textContent = value;
		}
	};

	/**
  * Checks if the character passed in is an Arabic character.
  * @param {string} charCode - Character code
  * @return {boolean} True, if character is an Arabic character
  */
	BidiUtil.prototype._isArabicChar = function (charCode) {
		if (charCode >= 0x0600 && charCode <= 0x0669 || charCode >= 0x06fa && charCode <= 0x07ff || charCode >= 0xfb50 && charCode <= 0xfdff || charCode >= 0xfe70 && charCode <= 0xfefc) {
			return true;
		}
		return false;
	};

	/**
  * Checks if the character passed in is a Hebrew character.
  * @param {string} charCode - Character code
  * @return {boolean} True, if character is a Hebrew character
  */
	BidiUtil.prototype._isHebrewChar = function (charCode) {
		if (charCode >= 0x05d0 && charCode <= 0x05ff) {
			return true;
		}
		return false;
	};

	/**
  * Checks if the character passed in is a BiDi character.
  * @param  {number}  charCode - Chracter code
  * @return {boolean} True, if the character is a BiDi (Arabic or Hebrew) character
  */
	BidiUtil.prototype._isBidiChar = function (charCode) {
		return this._isArabicChar(charCode) || this._isHebrewChar(charCode);
	};

	/**
  * Checks if the character passed in is a Latin character (Only treats
  * capital and lower case alphabets as latin characters)
  * @param  {number}  charCode - Character code
  * @return {boolean} True, if the character is a Latin character
  */
	BidiUtil.prototype._isLatinChar = function (charCode) {
		if (charCode > 64 && charCode < 91 || charCode > 96 && charCode < 123) {
			return true;
		}
		return false;
	};

	/**
  * Event handler for 'input' event (events causing the value of the target
  * element to change)
  * Resolves the base text direction and sets the dir attribute
  * @param {Event} event - DOM Event object
  */
	BidiUtil.prototype._handleInputEvent = function (event) {
		// NOTE: 'this' has been binded to be BidiUtil in _addBidiEventListeners function
		this._resolveDirAttr(event.target);
	};

	/**
  * Adds event listeners for events changing the value of the element being passed in
  * @param {Element|HTMLInputElement} element - DOM element receiving text input
  */
	BidiUtil.prototype._addBidiEventListeners = function (element) {
		if (!element._hasBidiEventListeners) {
			element._hasBidiEventListeners = true;

			var eventTypes = ['keyup', 'cut', 'paste'];
			for (var i = 0; i < eventTypes.length; ++i) {
				element.addEventListener(eventTypes[i], this._handleInputEvent.bind(this), false);
			}
		}
	};

	/**
  * Sets dir attribute of the DOM element passed in as parameter according to
  * the direction of the first strong character in the text content of the element
  * @param {Element|HTMLInputElement} element - DOM element receiving text input
  */
	BidiUtil.prototype._resolveDirAttr = function (element) {
		if (this._isIE && (this.userPreferredTextDir === AUTO || !this.userPreferredTextDir)) {
			// IE doesn't support dir="auto" HTML attribute
			var text = this._getNodeValue(element);
			element.dir = this.resolveBaseTextDir(text);
		} else {
			// Chrome, Safari, and Firefox supports dir="auto" HTML attribute
			// IE will only receive "ltr" or "rtl" in this block
			element.dir = this.userPreferredTextDir || AUTO;
		}
	};

	/**
 * This function determines the positions where we should insert the LRM marker for correct display
 * of Structured Text Value
 * @param str
 * @param isLocation
 * @returns array
 */
	BidiUtil.prototype._parseSTT = function (str, isLocation) {
		var delimiter = isLocation ? '>' : ':/@=[]\'<>';
		var segmentsPointers = [];
		var sp_len = 0,
		    i;

		for (i = 0; i < str.length; i++) {
			if (delimiter.indexOf(str.charAt(i)) >= 0) {
				segmentsPointers[sp_len] = i;
				sp_len++;
			}
		}
		return segmentsPointers;
	};

	// Public Methods
	/**
  * Simply resolves the dir attribute of the HTML element and adds BiDi specific
  * event handlers if necessary
  * @param  {Element|HTMLInputElement} element - DOM element receiving text input
  */
	BidiUtil.prototype.initElementForBidi = function (element) {
		if (element) {
			this._resolveDirAttr(element); // resolve dir attribute of the element

			if (this._isIE) {
				this._addBidiEventListeners(element);
			}
		}
	};

	/**
  * Enforces text direction by adding UCC (Unicode Control Characters)
  * @param  {String} text - The text for which we should enforce text direction.
  * @returns string
  */
	BidiUtil.prototype.enforceTextDirection = function (text) {
		if (text) {
			var finalDir = this.resolveBaseTextDir(text);
			var finalValue = text;

			if (finalDir === 'ltr') {
				finalValue = this.LRE + finalValue + this.PDF;
			} else if (finalDir === 'rtl') {
				finalValue = this.RLE + finalValue + this.PDF;
			}
			return finalValue;
		}
		return text;
	};

	/**
  * Traverses the string passed in as parameter from the beginning and
  * determines the direction of the text based on the first strong character
  * @param  {string} text - A bi-directional text
  * @param  {bool} isTextArea - Does the text belong to a textarea
  * @return {string} Direction of the text
  */
	BidiUtil.prototype.resolveBaseTextDir = function (text, isTextArea) {

		var textDir = this.userPreferredTextDir;
		if (!textDir) {
			textDir = AUTO;
		}
		if (textDir === AUTO && (!isTextArea || this._isIE)) {
			for (var i = 0; text && i < text.length; i++) {
				var character = text.charCodeAt(i);
				if (this._isBidiChar(character)) {
					textDir = 'rtl';
					break;
				} else if (this._isLatinChar(character)) {
					textDir = 'ltr';
					break;
				}
			}
			if (this._isIE && textDir === AUTO) {
				textDir = '';
			}
		}

		return textDir;
	};

	/**
  * Enforces text direction for Structured Text value by adding UCC (Unicode Control Characters)
  * We should add an LRM before each segment and also we should enforce text direction of each segment
  * @param  {String} text - The text for which we should enforce text direction.
  * @returns string
  */
	BidiUtil.prototype.enforceTextDirectionForSTT = function (text) {
		if (text) {
			text = this.removeMarkers(text);
			var isLocation = (text.match(/ > /g) || []).length > 0;
			var segmentsPointers = this._parseSTT(text, isLocation);
			var result = '';
			var n;
			var marker = this.LRM;
			var offset = isLocation ? 1 : 0;
			if (segmentsPointers.length === 0) {
				result = this.enforceTextDirection(text);
			} else {
				result = this.enforceTextDirection(text.substring(0, segmentsPointers[0] - offset));
			}
			for (var i = 0; i < segmentsPointers.length; i++) {
				n = segmentsPointers[i];
				if (n) {
					var endIndex = i < segmentsPointers.length - 1 ? segmentsPointers[i + 1] - offset : text.length;
					var segment = text.substring(n + 1 + offset, endIndex);
					result = result + marker + text.substring(n - offset, n + offset + 1) + this.enforceTextDirection(segment);
				}
			}
			return result;
		}
		return text;
	};

	/**
  * Enforces text direction for Structured Text value by adding UCC (Unicode Control Characters)
  * We should add an LRM before each segment and also we should enforce text direction of each segment
  * @param  {String} text - The text for which we should enforce text direction.
  * @returns string
  */
	BidiUtil.prototype.enforceTextDirectionForLocation = function (text) {
		return this.enforceTextDirectionForSTT(text);
	};

	/**
  * Removes all the markers from the text
  * @param  {String} text - The text
  * @returns string
  */
	BidiUtil.prototype.removeMarkers = function (text) {
		return text.replace(/[\u202A\u202B\u202C\u200E]/g, '');
	};

	BidiUtil.prototype._isArabicLocale = function (locale) {
		return locale.match(/^ar[-_].*$/i);
	};

	BidiUtil.prototype._useLatinNums = function (locale) {
		if (!this._isArabicLocale(locale)) {
			return true;
		}
		return cldrData[locale] && cldrData[locale] === 'latn';
	};

	/**
  * Enforces numeric shaping for the given string
  * @param {Object} text to be wrapped. Can be number or string object.
  * @param {bool} isContextual. Should enforce contextual numeric shaping.
  * @returns {string}
  */
	BidiUtil.prototype.enforceNumericShaping = function (text, isContextual) {
		if (this.bidiSupport && this.userPreferredContentLocale.startsWith('ar') && (typeof text === 'number' || typeof text === 'string')) {
			var segmentDir = this.userPreferredTextDir;
			if (this.userPreferredTextDir === AUTO) {
				segmentDir = this.resolveBaseTextDir(text);
			}
			var locale = typeof navigator === 'undefined' ? '' : navigator.language || navigator.userLanguage || '';
			locale = locale.replace('-', '_');
			var finalText = text;
			if (typeof text === 'number') {
				finalText = text.toString();
			}
			// eslint-disable-next-line no-misleading-character-class
			var pattern = /([0-9])|([\u0660-\u0669])|([\u0590-\u05FF\u0608\u060B\u060D\u061B-\u064A\u066D-\u066F\u0671-\u06D5\u06E5-\u06E6\u06EE-\u06EF\u06FA-\u06FF\u0750-\u077F\u08A0-\u08E3\u200F\u202B\u202E\u2067\uFB50-\uFD3D\uFD40-\uFDCF\uFDF0-\uFDFC\uFDFE-\uFDFF\uFE70-\uFEFE]+)|([^0-9\u0590-\u05FF\u0660-\u0669\u0608\u060B\u060D\u061B-\u064A\u066D-\u066F\u0671-\u06D5\u06E5-\u06E6\u06EE-\u06EF\u06FA-\u06FF\u0750-\u077F\u08A0-\u08E3\u200F\u202B\u202E\u2067\uFB50-\uFD3D\uFD40-\uFDCF\uFDF0-\uFDFC\uFDFE-\uFDFF\uFE70-\uFEFE\u0600-\u0607\u0609-\u060A\u060C\u060E-\u061A\u064B-\u066C\u0670\u06D6-\u06E4\u06E7-\u06ED\u06F0-\u06F9\u08E4-\u08FF\uFD3E-\uFD3F\uFDD0-\uFDEF\uFDFD\uFEFF\u0000-\u0040\u005B-\u0060\u007B-\u007F\u0080-\u00A9\u00AB-\u00B4\u00B6-\u00B9\u00BB-\u00BF\u00D7\u00F7\u02B9-\u02BA\u02C2-\u02CF\u02D2-\u02DF\u02E5-\u02ED\u02EF-\u02FF\u2070\u2074-\u207E\u2080-\u208E\u2100-\u2101\u2103-\u2106\u2108-\u2109\u2114\u2116-\u2118\u211E-\u2123\u2125\u2127\u2129\u212E\u213A-\u213B\u2140-\u2144\u214A-\u214D\u2150-\u215F\u2189\uA720-\uA721\uA788\uFF01-\uFF20\uFF3B-\uFF40\uFF5B-\uFF65\uFFE0-\uFFE6\uFFE8-\uFFEE]+)/g; // eslint-disable-line no-control-regex
			var self = this;
			return finalText.replace(pattern, function (curChs, latNum, araNum, rtlChs, ltrChs) {
				if (araNum) {
					if (isContextual && segmentDir === 'ltr' || !isContextual && self._useLatinNums(locale)) {
						return araNum.charCodeAt(0) - 1632;
					} else {
						return araNum;
					}
				} else if (latNum) {
					if (isContextual && segmentDir === 'rtl' || !isContextual && !self._useLatinNums(locale)) {
						return String.fromCharCode(parseInt(latNum) + 1632);
					} else {
						return latNum;
					}
				} else if (rtlChs) {
					segmentDir = 'rtl';
				} else if (ltrChs) {
					segmentDir = 'ltr';
				}
				return curChs;
			});
		}
		return text;
	};

	/**
  * Sets the text dir user preference
  * @param  {String} textDir - The text direction user preference.
  * @param  {String} bidiSupport - Is Bidi support enabled ?
  */
	BidiUtil.prototype.setUserPreferredTextDir = function (textDir, bidiSupport) {
		this.userPreferredTextDir = '';
		this.bidiSupport = false;
		if (bidiSupport === 'true') {
			this.userPreferredTextDir = textDir.toLowerCase();
			this.bidiSupport = true;
		}
	};

	/**
  * Sets the content locale user preference
  * @param  {String} contentLocale - The content locale user preference.
  */
	BidiUtil.prototype.setUserPreferredContentLocale = function (contentLocale) {
		this.userPreferredContentLocale = contentLocale;
	};

	return new BidiUtil();
});
//# sourceMappingURL=BidiUtil.js.map
//# sourceMappingURL=BidiUtil.js.map



// WEBPACK FOOTER //
// ./src/js/lib/@waca/core-client/js/core-client/utils/BidiUtil.js